"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { GripHorizontal } from "lucide-react";
import { Attachment, AttachmentContent, AttachmentDescription, AttachmentMedia, AttachmentTitle } from "@/components/ui/attachment";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageScroller, MessageScrollerButton, MessageScrollerContent, MessageScrollerItem, MessageScrollerProvider, MessageScrollerViewport } from "@/components/ui/message-scroller";
import { Bubble, BubbleContent } from "@/components/ui/bubble";
import { Message, MessageAvatar, MessageContent, MessageFooter, MessageHeader } from "@/components/ui/message";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DEFAULT_SOCIETY_NAME } from "@/lib/site-settings";

export type ReviewComment = { id: string; author_name: string; author_role?: "owner" | "client"; content: string; created_at?: string; profile_photo_url?: string | null };
export type ReviewInitial = { author_name: string; content: string; title?: string | null; author_role: "client"; created_at?: string; profile_photo_url?: string | null; attachment_url?: string | null; attachment_name?: string | null };

function initials(name: string) { return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "C"; }

function formatReviewDate(value: string) {
  const date = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function ReviewImageZoom({ src, name }: { src: string; name: string }) {
  return <Dialog>
    <DialogTrigger asChild>
      <button type="button" className="review-message-attachment-trigger" aria-label={`Ouvrir ${name} en grand`}>
        <Attachment size="sm">
          <AttachmentMedia variant="image"><Image src={src} alt="" width={64} height={64} /></AttachmentMedia>
          <AttachmentContent><AttachmentTitle>{name}</AttachmentTitle><AttachmentDescription>Ouvrir la photo</AttachmentDescription></AttachmentContent>
        </Attachment>
      </button>
    </DialogTrigger>
    <DialogContent className="review-image-dialog">
      <DialogTitle className="sr-only">{name}</DialogTitle>
      <DialogDescription className="sr-only">Aperçu agrandi de la photo jointe à l’avis.</DialogDescription>
      <Image src={src} alt={name} width={1400} height={1000} className="review-image-dialog-image" />
    </DialogContent>
  </Dialog>;
}

function ReviewMessage({ authorName, role, content, title, createdAt, profilePhotoUrl, attachmentUrl, photoName }: { authorName: string; role: "client" | "owner"; content: string; title?: string | null; createdAt?: string; profilePhotoUrl?: string | null; attachmentUrl?: string | null; photoName?: string | null }) {
  const align = role === "owner" ? "end" : "start";
  return <Message align={align} className={`review-message ${role === "client" ? "review-message-client" : "review-message-owner"}`}>
    <MessageAvatar className="review-message-avatar"><Avatar size="sm">{(role === "owner" ? "/og/elenails-hair.jpg" : profilePhotoUrl) && <AvatarImage src={role === "owner" ? "/og/elenails-hair.jpg" : profilePhotoUrl || undefined} alt={role === "owner" ? DEFAULT_SOCIETY_NAME : ""} />}<AvatarFallback>{role === "owner" ? "EH" : initials(authorName)}</AvatarFallback></Avatar></MessageAvatar>
    <MessageContent>
      <MessageHeader>{authorName}<span className="review-message-role">{role === "client" ? "Avis cliente" : DEFAULT_SOCIETY_NAME}</span></MessageHeader>
      <Bubble align={align} variant={role === "client" ? "outline" : "tinted"}><BubbleContent>{title && <strong className="review-message-title">{title}</strong>}<span>{content}</span>{attachmentUrl && <ReviewImageZoom src={attachmentUrl} name={photoName || "Photo jointe"} />}</BubbleContent></Bubble>
      {createdAt && <MessageFooter>{formatReviewDate(createdAt)}</MessageFooter>}
    </MessageContent>
  </Message>;
}

export function ReviewConversation({ initial, comments, composer }: { initial?: ReviewInitial; comments: ReviewComment[]; composer?: ReactNode }) {
  const [height, setHeight] = useState(250);
  const resizeStart = useRef<{ pointerId: number; y: number; height: number } | null>(null);

  useEffect(() => {
    function handlePointerMove(event: PointerEvent) {
      if (!resizeStart.current) return;
      const nextHeight = resizeStart.current.height + (event.clientY - resizeStart.current.y);
      setHeight(Math.min(420, Math.max(220, nextHeight)));
    }
    function handlePointerUp() {
      resizeStart.current = null;
      document.body.classList.remove("is-resizing-review");
    }
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, []);

  function startResize(event: ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeStart.current = { pointerId: event.pointerId, y: event.clientY, height };
    document.body.classList.add("is-resizing-review");
  }

  function resizeWithKeyboard(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown" && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    const nextHeight = event.key === "Home" ? 220 : event.key === "End" ? 420 : height + (event.key === "ArrowDown" ? 20 : -20);
    setHeight(Math.min(420, Math.max(220, nextHeight)));
  }

  if (!initial && !comments.length) return null;
  return <MessageScrollerProvider defaultScrollPosition="last-anchor"><div className="review-conversation-resizable" style={{ "--review-conversation-height": `${height}px` } as CSSProperties}><MessageScroller className="review-message-scroller"><MessageScrollerViewport aria-label="Conversation de l’avis"><MessageScrollerContent>{initial && <MessageScrollerItem messageId="review" scrollAnchor><ReviewMessage authorName={initial.author_name} role="client" content={initial.content} title={initial.title} createdAt={initial.created_at} profilePhotoUrl={initial.profile_photo_url} attachmentUrl={initial.attachment_url} photoName={initial.attachment_name} /></MessageScrollerItem>}{comments.map((comment) => <MessageScrollerItem messageId={`comment-${comment.id}`} key={comment.id}><ReviewMessage authorName={comment.author_name} role={comment.author_role === "client" ? "client" : "owner"} content={comment.content} createdAt={comment.created_at} profilePhotoUrl={comment.profile_photo_url} /></MessageScrollerItem>)}</MessageScrollerContent></MessageScrollerViewport>{composer && <div className="review-message-composer">{composer}</div>}<MessageScrollerButton aria-label="Faire défiler la conversation" /></MessageScroller><button type="button" role="slider" className="review-resize-handle" aria-label="Ajuster la hauteur de la conversation" aria-orientation="vertical" aria-valuemin={220} aria-valuemax={420} aria-valuenow={height} onPointerDown={startResize} onKeyDown={resizeWithKeyboard}><GripHorizontal /></button></div></MessageScrollerProvider>;
}
